(() => {
  let overlay = null;
  let audioCtx = null;
  let hideTimer = null;
  let lastFireAt = 0;
  let pendingTimer = null;
  let audioBuffer = null;
  let audioLoadPromise = null;

  const isAddButton = (el) => {
    if (!el || !el.closest) return false;
    return Boolean(
      el.closest(
        [
          'product-form [type="submit"]',
          'product-form .product-form__submit',
          '.product-form__submit',
          'button[name="add"]',
          'input[name="add"]',
          '.quick-add__submit',
          'form[action*="/cart/add"] [type="submit"]',
        ].join(', ')
      )
    );
  };

  const ensureOverlay = () => {
    if (overlay && document.body.contains(overlay)) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'rfrb-hitmarker';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML =
      '<div class="rfrb-hitmarker__x">' +
      '<span class="rfrb-hitmarker__line rfrb-hitmarker__line--a"></span>' +
      '<span class="rfrb-hitmarker__line rfrb-hitmarker__line--b"></span>' +
      '</div>';
    document.body.appendChild(overlay);
    return overlay;
  };

  const getAudioCtx = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }
    return audioCtx;
  };

  const loadHitSample = () => {
    if (audioBuffer) return Promise.resolve(audioBuffer);
    if (audioLoadPromise) return audioLoadPromise;
    const url = window.rfrbHitmarkerSoundUrl;
    if (!url) return Promise.resolve(null);

    audioLoadPromise = fetch(url)
      .then((r) => r.arrayBuffer())
      .then(
        (buf) =>
          new Promise((resolve, reject) => {
            const ctx = getAudioCtx();
            if (!ctx) {
              resolve(null);
              return;
            }
            ctx.decodeAudioData(
              buf,
              (decoded) => {
                audioBuffer = decoded;
                resolve(decoded);
              },
              reject
            );
          })
      )
      .catch(() => null);

    return audioLoadPromise;
  };

  // Fallback matching: crisp HF double-transient, HPF ~2kHz, ~20ms decay, no low-end
  const playSynthFallback = (ctx) => {
    const now = ctx.currentTime;

    const fireClick = (offset, peak) => {
      const t0 = now + offset;
      const noiseDur = 0.012;
      const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * noiseDur), ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 5);
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 2000;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 4500;
      bp.Q.value = 0.8;
      const g = ctx.createGain();
      g.gain.setValueAtTime(peak, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.02);
      noise.connect(hp);
      hp.connect(bp);
      bp.connect(g);
      g.connect(ctx.destination);
      noise.start(t0);
      noise.stop(t0 + noiseDur);

      [4200, 6100].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const og = ctx.createGain();
        og.gain.setValueAtTime(peak * (idx === 0 ? 0.45 : 0.25), t0);
        og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.018);
        const ohp = ctx.createBiquadFilter();
        ohp.type = 'highpass';
        ohp.frequency.value = 2000;
        osc.connect(ohp);
        ohp.connect(og);
        og.connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.02);
      });
    };

    // Micro-second double-transient
    fireClick(0, 0.14);
    fireClick(0.0012, 0.1);
  };

  const playHitSound = () => {
    try {
      const ctx = getAudioCtx();
      if (!ctx) return;

      const playBuffer = (buf) => {
        if (!buf) {
          playSynthFallback(ctx);
          return;
        }
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const hp = ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 2000;
        const gain = ctx.createGain();
        gain.gain.value = 0.75;
        src.connect(hp);
        hp.connect(gain);
        gain.connect(ctx.destination);
        src.start(0);
      };

      if (audioBuffer) {
        playBuffer(audioBuffer);
        return;
      }

      loadHitSample().then((buf) => playBuffer(buf));
    } catch (e) {
      // Ignore audio autoplay / unsupported contexts
    }
  };

  const vibrate = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof navigator.vibrate === 'function') {
      navigator.vibrate(16);
    }
  };

  const showHitmarker = (x, y) => {
    const el = ensureOverlay();
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.classList.remove('is-active');
    void el.offsetWidth;
    el.classList.add('is-active');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      el.classList.remove('is-active');
    }, 300);
  };

  const fire = (x, y) => {
    const now = Date.now();
    if (now - lastFireAt < 180) return;
    lastFireAt = now;
    showHitmarker(x, y);
    playHitSound();
    vibrate();
  };

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    if (!isAddButton(event.target)) return;

    const x = event.clientX;
    const y = event.clientY;
    getAudioCtx();
    loadHitSample();

    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => fire(x, y), 30);
  };

  document.addEventListener('pointerdown', onPointerDown, true);

  const boot = () => {
    ensureOverlay();
    // Warm decode early so first click is instant
    try {
      getAudioCtx();
      loadHitSample();
    } catch (e) {}
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
